import { GitHubRepo } from "@/lib/types/github";
import { env } from "./env";

const GITHUB_API = "https://api.github.com";

/**
 * Fetch public repositories for a GitHub user
 */
export async function fetchGitHubRepos(
  username: string,
): Promise<GitHubRepo[]> {
  try {
    // Using /users/{username}/repos with type=public doesn't work reliably
    // So we fetch all and filter client-side for public repos only
    const response = await fetch(
      `${GITHUB_API}/users/${username}/repos?sort=updated&per_page=100&visibility=public`,
      {
        headers: {
          Accept: "application/vnd.github.v3+json",
          // Add your GitHub token here for higher rate limits (optional)
          Authorization: `Bearer ${env.NEXT_PUBLIC_GITHUB_TOKEN}`,
        },
        next: { revalidate: 3600 }, // Cache for 1 hour
      },
    );

    if (!response.ok) {
      throw new Error(`GitHub API error: ${response.status}`);
    }

    const repos: GitHubRepo[] = await response.json();

    // Filter: only public repos (not private, not forks)
    // The 'private' field is the definitive check
    return repos.filter((repo) => !repo.fork && repo.private === false);
  } catch (error) {
    console.error("Error fetching GitHub repos:", error);
    return [];
  }
}

/**
 * Fetch languages used in a repository
 */
export async function fetchRepoLanguages(
  languagesUrl: string,
): Promise<string[]> {
  try {
    const response = await fetch(languagesUrl, {
      headers: {
        Accept: "application/vnd.github.v3+json",
      },
      next: { revalidate: 3600 },
    });

    if (!response.ok) {
      return [];
    }

    const languages = await response.json();

    console.log("Fetched languages:", languages);

    // Sort by bytes and return top 4
    return Object.entries(languages)
      .sort(([, a], [, b]) => (b as number) - (a as number))
      .slice(0, 4)
      .map(([lang]) => lang);
  } catch (error) {
    console.error("Error fetching repo languages:", error);
    return [];
  }
}

/**
 * Check if a URL is accessible
 */
export async function checkUrlExists(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: "HEAD" });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Try to find a project screenshot
 */
export async function findProjectImage(
  username: string,
  repoName: string,
  homepage: string | null,
): Promise<string | null> {
  // Priority 1: If homepage exists, capture a screenshot of the live site
  if (homepage && isValidUrl(homepage)) {
    try {
      // Use a screenshot API to capture the deployed site
      const screenshotUrl = await captureScreenshot(homepage);
      if (screenshotUrl) {
        return screenshotUrl;
      }
    } catch (error) {
      console.error("Error capturing screenshot:", error);
    }
  }

  // Priority 2: Check /public/images/icon.png in repo
  const iconPath = `https://raw.githubusercontent.com/${username}/${repoName}/main/public/images/icon.png`;

  try {
    const iconResponse = await fetch(iconPath, { method: "HEAD" });
    if (iconResponse.ok) {
      return iconPath;
    }
  } catch {
    // Continue to next option
  }

  // Priority 3: Check common screenshot locations
  const commonPaths = [
    `https://raw.githubusercontent.com/${username}/${repoName}/main/screenshot.png`,
    `https://raw.githubusercontent.com/${username}/${repoName}/main/preview.png`,
    `https://raw.githubusercontent.com/${username}/${repoName}/main/docs/screenshot.png`,
    `https://raw.githubusercontent.com/${username}/${repoName}/main/public/screenshot.png`,
    `https://raw.githubusercontent.com/${username}/${repoName}/main/.github/preview.png`,
  ];

  for (const path of commonPaths) {
    try {
      const response = await fetch(path, { method: "HEAD" });
      if (response.ok) {
        return path;
      }
    } catch {
      continue;
    }
  }

  // Priority 4: Try Open Graph image
  if (homepage) {
    try {
      const ogImage = await fetchOgImage(homepage);
      if (ogImage) return ogImage;
    } catch {
      // Continue to default
    }
  }

  return null;
}

/**
 * Capture a screenshot of a live website
 * Uses a free screenshot API service
 */
export async function captureScreenshot(url: string): Promise<string | null> {
  try {
    // Option 1: Using screenshotone.com
    const apiKey = env.NEXT_PUBLIC_SCREENSHOT_API_KEY;

    if (apiKey) {
      const screenshotUrl = `https://api.screenshotone.com/take?access_key=${apiKey}&url=${encodeURIComponent(url)}&viewport_width=1920&viewport_height=1080&device_scale_factor=1&format=jpg&image_quality=80&block_ads=true&block_cookie_banners=true&block_trackers=true&delay=3&timeout=60`;

      try {
        const response = await fetch(screenshotUrl, {
          method: "HEAD",
          signal: AbortSignal.timeout(5000), // 5 second timeout
        });

        if (response.ok) {
          return screenshotUrl;
        }
      } catch (error) {
        console.warn("Screenshotone failed, trying alternatives");
      }
    }

    // Option 2: Using Microlink API
    try {
      const microlinkUrl = `https://api.microlink.io/?url=${encodeURIComponent(url)}&screenshot=true&meta=false&embed=screenshot.url`;

      const response = await fetch(microlinkUrl, {
        signal: AbortSignal.timeout(10000), // 10 second timeout
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === "success" && data.data?.screenshot?.url) {
          return data.data.screenshot.url;
        }
      }
    } catch (error) {
      console.warn("Microlink failed, trying alternatives");
    }

    // Option 3: Using Vercel's OG Image as fallback
    // This creates a simple preview card
    const vercelOgUrl = `https://og-image.vercel.app/${encodeURIComponent(url)}.png`;

    try {
      const response = await fetch(vercelOgUrl, {
        method: "HEAD",
        signal: AbortSignal.timeout(5000),
      });

      if (response.ok) {
        return vercelOgUrl;
      }
    } catch (error) {
      console.warn("Vercel OG fallback failed");
    }

    return null;
  } catch (error) {
    console.error("Error capturing screenshot:", error);
    return null;
  }
}

/**
 * Check if a string is a valid URL
 */
function isValidUrl(string: string): boolean {
  try {
    const url = new URL(string);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Fetch Open Graph image from a URL
 */
async function fetchOgImage(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    const html = await response.text();

    const ogImageMatch = html.match(
      /<meta\s+property=["']og:image["']\s+content=["']([^"']+)["']/i,
    );

    return ogImageMatch ? ogImageMatch[1] : null;
  } catch {
    return null;
  }
}
